import { useCallback, useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import {
  useErpSurveyAnalyticsQuery,
  useErpSurveyCommentsQuery,
  useErpSurveyQuestionBreakdownQuery,
  useErpSurveySignalsQuery,
} from 'core/graphql';

import {
  ISurveyAnalyticsFilter,
  ISurveyAnalyticsRow,
  ISurveyComment,
  ISurveySignal,
  SurveyDimension,
} from './analyticsModel';

/**
 * Spec 0038 — the analytics reads. All four share ONE filter object, which is
 * why the backend takes an input type rather than flat args: the filter panel
 * holds a single piece of state and hands the same value to every hook.
 */

/** Drops empty arrays/strings so an untouched filter sends nothing at all. */
const cleanFilter = (
  filter?: ISurveyAnalyticsFilter,
): ISurveyAnalyticsFilter | undefined => {
  if (!filter) return undefined;
  const entries = Object.entries(filter).filter(([, value]) => {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
  return entries.length
    ? (Object.fromEntries(entries) as ISurveyAnalyticsFilter)
    : undefined;
};

export const useSurveyAnalytics = (
  dimension: SurveyDimension,
  filter?: ISurveyAnalyticsFilter,
  /**
   * `skip` — роль `surveyAnalytics` не читает (`TEACHER_INSIGHT_ROLES`):
   * запрос не уходит (карточка группы открыта шире опросов).
   */
  options: { skip?: boolean } = {},
): {
  rows: ISurveyAnalyticsRow[];
  loading: boolean;
  denied: boolean;
  error?: ApolloError;
  refetch: () => void;
} => {
  const skipped = Boolean(options.skip);
  const variables = useMemo(
    () => ({ dimension, filter: cleanFilter(filter) }),
    [dimension, filter],
  );
  const { data, loading, error, refetch } = useErpSurveyAnalyticsQuery({
    variables,
    skip: skipped,
    fetchPolicy: 'cache-and-network',
  });
  return {
    rows: (data?.surveyAnalytics || []) as ISurveyAnalyticsRow[],
    loading,
    denied: Boolean(error),
    error,
    refetch: useCallback(() => {
      // Под skip Apollo 3.3 ушёл бы с refetch в сеть — тот самый запрос.
      if (skipped) return;
      refetch().catch(() => undefined);
    }, [refetch, skipped]),
  };
};

export const useSurveySignals = (
  filter?: ISurveyAnalyticsFilter,
  dimensions: string[] = ['group', 'teacher'],
  minSample?: number,
): { signals: ISurveySignal[]; loading: boolean; denied: boolean } => {
  const { data, loading, error } = useErpSurveySignalsQuery({
    variables: { dimensions, minSample, filter: cleanFilter(filter) },
    fetchPolicy: 'cache-and-network',
  });
  return {
    signals: (data?.surveySignals || []) as ISurveySignal[],
    loading,
    denied: Boolean(error),
  };
};

interface ICommentsHook {
  comments: ISurveyComment[];
  hasNext: boolean;
  loading: boolean;
  /**
   * The query was refused, which is NOT the same as an empty feed. Rendering
   * «комментариев нет» to a role that simply may not read them tells them
   * students wrote nothing — the most misleading answer available.
   */
  denied: boolean;
  /** The raw failure — tells a permission refusal from a server error. */
  error?: ApolloError;
  refetch: () => void;
  loadMore: () => void;
}

export const useSurveyComments = (
  filter?: ISurveyAnalyticsFilter,
  search?: string,
  sentiment?: string,
  /** Spec 0098: only answers the AI marked as a problem (enriched ones). */
  problemOnly?: boolean,
): ICommentsHook => {
  const {
    data,
    loading,
    error,
    fetchMore,
    refetch,
  } = useErpSurveyCommentsQuery({
    variables: {
      filter: cleanFilter(filter),
      search,
      sentiment,
      first: 25,
      problemOnly: problemOnly || undefined,
    },
    fetchPolicy: 'cache-and-network',
  });
  const page = data?.surveyComments;

  return {
    comments: (page?.items || []) as ISurveyComment[],
    hasNext: Boolean(page?.hasNext),
    loading,
    denied: Boolean(error),
    error,
    refetch: () => {
      refetch().catch(() => undefined);
    },
    // Keyset, not offset: the feed is newest-first while new responses keep
    // arriving, so a page number would repeat or skip rows as the head moves.
    loadMore: () => {
      if (!page?.cursor) return;
      fetchMore({
        variables: { after: page.cursor },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (!fetchMoreResult?.surveyComments) return previous;
          return {
            ...fetchMoreResult,
            surveyComments: {
              ...fetchMoreResult.surveyComments,
              items: [
                ...(previous.surveyComments?.items || []),
                ...fetchMoreResult.surveyComments.items,
              ],
            },
          };
        },
      });
    },
  };
};

export const useSurveyQuestionBreakdown = (
  surveyId?: string,
  questionId?: string,
  dimension: SurveyDimension = 'group',
  filter?: ISurveyAnalyticsFilter,
) => {
  const { data, loading } = useErpSurveyQuestionBreakdownQuery({
    variables: {
      surveyId: surveyId as string,
      questionId: questionId as string,
      dimension,
      filter: cleanFilter(filter),
    },
    skip: !surveyId || !questionId,
    fetchPolicy: 'cache-and-network',
  });
  return {
    breakdown: data?.surveyQuestionBreakdown || undefined,
    loading,
  };
};
